import React, { useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { useTheme } from 'hooks/useTheme';

gsap.registerPlugin(ScrollTrigger);

const FullPageImageAnimation = ({ data }) => {
  const { isDarkMode } = useTheme();
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const derRef = useRef(null);
  const raRef = useRef(null);
  const umRef = useRef(null);

  useGSAP(
    () => {
      const image = imageRef.current;
      const der = derRef.current;
      const ra = raRef.current;
      const um = umRef.current;

      if (!image || !der || !ra || !um || !data) return;

      const mm = gsap.matchMedia(containerRef);

      mm.add(
        {
          isDesktop: `(min-width: 769px)`,
          isMobile: `(max-width: 768px)`,
        },
        (context) => {
          const { isDesktop, isMobile } = context.conditions;

          if (isDesktop) {
            const initialWidthDVW = 27.78;
            const initialHeightDVH = 57.04;
            let initialTopInsetPx, initialLeftInsetPx;

            const updatePositions = (progress) => {
              const top = gsap.utils.mapRange(
                0,
                1,
                initialTopInsetPx,
                0,
                progress
              );
              const left = gsap.utils.mapRange(
                0,
                1,
                initialLeftInsetPx,
                0,
                progress
              );
              gsap.set(image, {
                clipPath: `inset(${top}px ${left}px ${top}px ${left}px)`,
              });
              gsap.set(der, { top: top, yPercent: -100 });
              gsap.set(ra, { left: left, xPercent: -100, yPercent: -50 });
              gsap.set(um, { right: left, xPercent: 100, yPercent: -50 });
            };

            const proxy = { progress: 0 };
            gsap.to(proxy, {
              progress: 1,
              ease: 'none',
              scrollTrigger: {
                trigger: containerRef.current,
                start: 'top top',
                end: '+=1500',
                scrub: true,
                pin: true,
                invalidateOnRefresh: true,
                onRefresh: (self) => {
                  initialTopInsetPx =
                    (window.innerHeight * (1 - initialHeightDVH / 100)) / 2;
                  initialLeftInsetPx =
                    (window.innerWidth * (1 - initialWidthDVW / 100)) / 2;
                  updatePositions(self.progress);
                },
                onUpdate: (self) => {
                  updatePositions(self.progress);
                },
              },
            });
            gsap.set([der, ra, um], { autoAlpha: 1 });
          }

          if (isMobile) {
            const initialWidthDVW = 86.46;
            const initialHeightDVH = 57.31;
            let initialTopInsetPx, initialLeftInsetPx, initialBottomInsetPx;
            let sideTitleContainer;

            sideTitleContainer = document.createElement('div');
            gsap.set(sideTitleContainer, {
              display: 'flex',
              justifyContent: 'center',
              textAlign: 'center',
              width: '100%',
            });
            ra.parentNode.insertBefore(sideTitleContainer, ra);
            sideTitleContainer.appendChild(ra);
            sideTitleContainer.appendChild(um);

            gsap.set([der, sideTitleContainer], {
              position: 'absolute',
              left: '50%',
              xPercent: -50,
            });
            gsap.set(der, { top: '8vh' });
            gsap.set(sideTitleContainer, { top: '17vh' });
            gsap.set([ra, um], {
              position: 'relative',
              top: 'auto',
              left: 'auto',
              right: 'auto',
              transform: 'none',
              padding: '0',
            });

            const updateMobilePositions = (progress) => {
              const top = gsap.utils.mapRange(
                0,
                1,
                initialTopInsetPx,
                0,
                progress
              );
              const left = gsap.utils.mapRange(
                0,
                1,
                initialLeftInsetPx,
                0,
                progress
              );
              const bottom = gsap.utils.mapRange(
                0,
                1,
                initialBottomInsetPx,
                0,
                progress
              );

              gsap.set(image, {
                clipPath: `inset(${top}px ${left}px ${bottom}px ${left}px)`,
              });

              gsap.set([der, sideTitleContainer], {
                y: top - initialTopInsetPx,
              });
            };

            const proxy = { progress: 0 };
            gsap.to(proxy, {
              progress: 1,
              ease: 'none',
              scrollTrigger: {
                trigger: containerRef.current,
                start: 'top top',
                end: '+=1000',
                scrub: true,
                pin: true,
                invalidateOnRefresh: true,
                onRefresh: (self) => {
                  const initialImageHeightPx =
                    window.innerHeight * (initialHeightDVH / 100);
                  const initialImageWidthPx =
                    window.innerWidth * (initialWidthDVW / 100);

                  initialBottomInsetPx = 48;
                  initialTopInsetPx =
                    window.innerHeight -
                    initialImageHeightPx -
                    initialBottomInsetPx;
                  initialLeftInsetPx =
                    (window.innerWidth - initialImageWidthPx) / 2;

                  updateMobilePositions(self.progress);
                },
                onUpdate: (self) => {
                  updateMobilePositions(self.progress);
                },
              },
            });

            gsap.set([der, ra, um], { autoAlpha: 1 });

            return () => {
              if (sideTitleContainer && sideTitleContainer.parentNode) {
                sideTitleContainer.parentNode.insertBefore(
                  ra,
                  sideTitleContainer
                );
                sideTitleContainer.parentNode.insertBefore(
                  um,
                  sideTitleContainer
                );
                sideTitleContainer.remove();
              }
            };
          }
        }
      );
    },
    { scope: containerRef }
  );

  return (
    <div
      ref={containerRef}
      className='fpia-container overflow-hidden full-pager relative full-page-image-anm'
    >
      <div className='full-pager sticky top-0'>
        <img
          ref={imageRef}
          src={
            isDarkMode
              ? data.darkModeImageUrl || data.lightModeImageUrl
              : data.lightModeImageUrl
          }
          alt='Revealed through a window'
          className='top-0 left-0 full-width full-height absolute'
        />
        <p
          ref={derRef}
          className='text-5xl-light font-secondary font-white-color transform-x-50'
          style={{
            left: '50%',
            paddingBottom: 'var(--space-32)',
          }}
        >
          {data.topSideTitle}
        </p>
        <p
          ref={raRef}
          className='font-extra-large font-white-color transform-y-50'
          style={{
            top: '50%',
            paddingRight: 'var(--space-32)',
          }}
        >
          {data.leftSideTitle}
        </p>
        <p
          ref={umRef}
          className='font-extra-large font-white-color transform-y-50'
          style={{
            top: '50%',
            paddingLeft: 'var(--space-24)',
          }}
        >
          {data.rightSideTitle}
        </p>
      </div>
    </div>
  );
};

export default FullPageImageAnimation;
